/**
 * Chakra UI v3 Theme Configuration
 *
 * This is THE theme file for Showpass. All colors adapt to dark mode.
 *
 * ## Architecture
 *
 * ```
 * colors.ts    → colorModes (raw values) + brandSemanticTokens (shared)
 * theme.ts     → THE theme (this file) - creates Chakra system
 * ```
 *
 * ## Key Principle
 *
 * EVERY color adapts to dark mode. There are no static palette tokens.
 * Use `gray.500`, `primary.500`, etc. and they automatically switch.
 *
 * ## SSR/Hydration
 *
 * `defaultSystem` is created once at module load and exported.
 * This prevents style flash on hydration.
 */
import {
  createSystem,
  defaultConfig,
  defineConfig,
  defineSlotRecipe,
} from "@chakra-ui/react";
import {
  colorModes,
  brandSemanticTokens,
  generateFullPalette,
  generateBgSubtleToken,
} from "./props/colors";
import { spacingTokens } from "./props/spacing";
import { radiiTokens } from "./props/radii";
import breakpoints, { breakpointsTheme } from "./props/breakpoints";
import {
  textStyles,
  fontSizeTokens,
  lineHeights,
  fontWeights,
} from "./props/textStyles";
import { zIndexTokens } from "./props/zIndexes";
import checkboxSlotRecipe from "./components/Checkbox";
import linkRecipe from "./components/Link";
import tagSlotRecipe from "./components/Tag";
import { ColorPalette } from "./types";
import {
  mergeTokenObjects,
  mergeRadiiTokenObjects,
  buildSemanticColorOverrides,
} from "./utils/theme-override-utils";
import { buildButtonRecipe } from "./recipes/button-recipe";
import type {
  ThemeTokenOverrides,
  ThemeComponentOverrides,
  WebsiteComponentDefaults,
} from "./types/theme-overrides";

// =============================================================================
// FONT CONSTANTS
// Shared fallback stack used in both defaultSystem and generateTheme()
// =============================================================================

/** Default font family */
const DEFAULT_FONT = "Lato, sans-serif";

/**
 * Build a CSS font-family value from an optional custom font name.
 * Falls back to Lato when no custom font is provided.
 * The browser naturally falls through the list if a font isn't available.
 */
export function buildFontStack(customFont?: string): string {
  if (!customFont) return DEFAULT_FONT;
  const quoted = customFont.includes(" ") ? `"${customFont}"` : customFont;
  return `${quoted}, sans-serif`;
}

type ThemeTokenObject = Record<string, { value: string }>;

function toTokenObject(values: Record<string, string>): ThemeTokenObject {
  return Object.entries(values).reduce<ThemeTokenObject>(
    (acc, [key, value]) => {
      acc[key] = { value };
      return acc;
    },
    {}
  );
}

const lineHeightTokens = toTokenObject(lineHeights);
const fontWeightTokens = toTokenObject(fontWeights);

export type {
  ThemeTokenOverrides,
  ThemeComponentOverrides,
  WebsiteComponentDefaults,
} from "./types/theme-overrides";

const DEFAULT_WEBSITE_COMPONENT_DEFAULTS: WebsiteComponentDefaults = {};

export function getWebsiteComponentDefaults(
  components?: ThemeComponentOverrides
): WebsiteComponentDefaults {
  if (!components) return DEFAULT_WEBSITE_COMPONENT_DEFAULTS;

  return {
    button: components.button,
    heading: components.heading,
    text: components.text,
    card: components.card,
    section: components.section,
  };
}

// =============================================================================
// LAYOUT CONSTANTS
// Export raw values for use in calc() expressions and non-Chakra contexts
// Token versions available as sizes: layout.headerHeight, layout.sidebarWidth, etc.
// =============================================================================
export const HEADER_HEIGHT = "72px";
export const SIDEBAR_WIDTH = "88px";
export const SUB_MENU_WIDTH = "256px"; // Slide-out panel width
export const NAVBAR_DESKTOP_HEIGHT = "75px";
export const NAVBAR_MOBILE_HEIGHT = "56px";

// =============================================================================
// STATIC COLOR TOKENS (truly static - don't change with mode)
// Only for absolute colors and third-party brand colors
// =============================================================================

const staticColors = {
  white: { value: "#FFFFFF" },
  black: { value: "#000000" },
  transparent: { value: "transparent" },
  // Third-party brand colors (truly static, never change)
  spotify: {
    green: { value: "#1db954" },
    black: { value: "#191414" },
  },
  stripe: {
    fontDark: { value: "#11142D" },
    gray400: { value: "#D1D5DB" },
    gray500: { value: "#9CA3AF" },
    gray600: { value: "#6B7280" },
    primary600: { value: "#0F4D52" },
    primary800: { value: "#073336" },
    secondary600: { value: "#CA1E1E" },
    red600: { value: "#EF4444" },
  },
};

// Raw hex exports for third-party APIs (Stripe JS, QR code lib)
// Returns mode-aware colors for Stripe elements that render inside iframes
export function getStripeColors(mode: "light" | "dark" = "light"): {
  fontDark: string;
  fontLight: string;
  gray400: string;
  gray500: string;
  gray600: string;
  primary600: string;
  primary800: string;
  secondary600: string;
  red600: string;
  muted: string;
  bg: string;
} {
  if (mode === "dark") {
    return {
      fontDark: "#11142D",
      fontLight: "#FFFFFF",
      gray400: "#6B7280",
      gray500: "#9CA3AF",
      gray600: "#D1D5DB",
      primary600: "#0F4D52",
      primary800: "#073336",
      secondary600: "#CA1E1E",
      red600: "#EF4444",
      muted: "#737373",
      bg: colorModes.white.dark,
    };
  }

  return {
    fontDark: "#11142D",
    fontLight: "#FFFFFF",
    gray400: "#D1D5DB",
    gray500: "#9CA3AF",
    gray600: "#6B7280",
    primary600: "#0F4D52",
    primary800: "#073336",
    secondary600: "#CA1E1E",
    red600: "#EF4444",
    muted: "#737373",
    bg: colorModes.white.light,
  };
}

// Backward compatibility: default export for light mode
export const STRIPE_COLORS = getStripeColors("light");

export const QR_CODE_COLORS = {
  foreground: staticColors.black.value,
  background: staticColors.white.value,
};

// =============================================================================
// SEMANTIC COLOR TOKENS
// ALL colors adapt to light/dark mode - no exceptions
// =============================================================================

const semanticTokens = {
  colors: {
    // === ALL COLOR PALETTES (numbered shades + semantic tokens) ===
    // Each palette gets both 100-950 shades AND .solid, .muted, .emphasized, etc.
    gray: generateFullPalette(colorModes.gray),
    red: generateFullPalette(colorModes.red),
    green: generateFullPalette(colorModes.green),
    yellow: generateFullPalette(colorModes.yellow),
    blue: generateFullPalette(colorModes.blue),
    purple: generateFullPalette(colorModes.purple),
    pink: generateFullPalette(colorModes.pink),
    indigo: generateFullPalette(colorModes.indigo),

    // === BRAND COLORS (CANNOT be overridden via generateTheme) ===
    // Use only for Showpass branding elements that should not change with custom themes,
    // otherwise always use primary or secondary.
    brandPrimary: generateFullPalette(colorModes.brandPrimary),
    brandSecondary: generateFullPalette(colorModes.brandSecondary),

    // === PRIMARY (dynamic - can be overridden via generateTheme) ===
    primary: generateFullPalette(colorModes.primary),
    ...generateBgSubtleToken(colorModes.primary, "primary"),

    // === SECONDARY (dynamic - can be overridden via generateTheme) ===
    secondary: generateFullPalette(colorModes.secondary),
    ...generateBgSubtleToken(colorModes.secondary, "secondary"),

    // === BASE SEMANTIC COLORS ===
    font: {
      value: { _light: colorModes.font.light, _dark: colorModes.font.dark },
    },
    "font.default": {
      value: { _light: colorModes.font.light, _dark: colorModes.font.dark },
    },
    charcoal: {
      value: {
        _light: colorModes.charcoal.light,
        _dark: colorModes.charcoal.dark,
      },
    },
    midnight: {
      value: {
        _light: colorModes.midnight.light,
        _dark: colorModes.midnight.dark,
      },
    },
    offWhite: {
      value: {
        _light: colorModes.offWhite.light,
        _dark: colorModes.offWhite.dark,
      },
    },
    "logo.fill": {
      value: { _light: colorModes.brandSecondary[600].light, _dark: "#FFFFFF" },
    },

    // === BACKGROUNDS ===
    bg: {
      value: { _light: colorModes.white.light, _dark: colorModes.white.dark },
    },
    "bg.subtle": {
      value: {
        _light: colorModes.offWhite.light,
        _dark: colorModes.offWhite.dark,
      },
    },
    "bg.muted": {
      value: {
        _light: colorModes.gray[100].light,
        _dark: colorModes.gray[100].dark,
      },
    },
    "bg.emphasized": {
      value: {
        _light: colorModes.gray[200].light,
        _dark: colorModes.gray[200].dark,
      },
    },
    "bg.inverted": {
      value: {
        _light: colorModes.gray[900].light,
        _dark: colorModes.gray[900].dark,
      },
    },

    // === FOREGROUNDS (TEXT) ===
    fg: {
      value: { _light: colorModes.font.light, _dark: colorModes.font.dark },
    },
    "fg.muted": {
      value: {
        _light: colorModes.gray[600].light,
        _dark: colorModes.gray[600].dark,
      },
    },
    "fg.subtle": {
      value: {
        _light: colorModes.gray[500].light,
        _dark: colorModes.gray[500].dark,
      },
    },
    "fg.inverted": {
      value: { _light: colorModes.white.light, _dark: colorModes.font.light },
    },

    // === LEGACY FONT TOKENS ===
    "font.dark": {
      value: { _light: colorModes.font.light, _dark: colorModes.font.dark },
    },
    "font.light": {
      value: { _light: "#FFFFFF", _dark: colorModes.font.light },
    },

    // === BORDERS ===
    border: {
      value: {
        _light: colorModes.gray[300].light,
        _dark: colorModes.gray[300].dark,
      },
    },
    "border.default": {
      value: {
        _light: colorModes.gray[300].light,
        _dark: colorModes.gray[300].dark,
      },
    },
    "border.muted": {
      value: {
        _light: colorModes.gray[200].light,
        _dark: colorModes.gray[200].dark,
      },
    },
    "border.emphasized": {
      value: {
        _light: colorModes.gray[500].light,
        _dark: colorModes.gray[500].dark,
      },
    },

    seatingLocationBackground: {
      value: {
        _light: colorModes.seatingLocationBackground.light,
        _dark: colorModes.seatingLocationBackground.dark,
      },
    },

    // === STATUS COLORS ===
    ...brandSemanticTokens,
  },
};

// =============================================================================
// THEME CONFIG
// =============================================================================

const accordionSlotRecipe = defineSlotRecipe({
  className: "accordion",
  slots: [
    "root",
    "item",
    "itemTrigger",
    "itemContent",
    "itemBody",
    "itemIndicator",
  ],
  base: {
    itemContent: { padding: 0 },
    itemBody: { padding: 0 },
  },
});

// =============================================================================
// SIZE TOKENS
//
// Named tokens (xs, sm, md, lg, xl) = BREAKPOINTS (for maxW, w, containers)
// Element tokens (element.xs, element.sm) = SMALL FIXED SIZES (icons, buttons)
// =============================================================================
const sizeTokens = {
  // Chakra default sizes (preserve existing behavior)
  // maxW="sm" = 384px, maxW="lg" = 512px
  xs: { value: "20rem" }, // 320px
  sm: { value: "24rem" }, // 384px
  md: { value: "28rem" }, // 448px
  lg: { value: "32rem" }, // 512px
  xl: { value: "36rem" }, // 576px
  "2xl": { value: "42rem" }, // 672px
  "3xl": { value: "48rem" }, // 768px
  "4xl": { value: "56rem" }, // 896px
  "5xl": { value: "64rem" }, // 1024px
  "6xl": { value: "72rem" }, // 1152px
  "7xl": { value: "80rem" }, // 1280px
  "8xl": { value: "90rem" }, // 1440px

  // Container sizes (match breakpoints for layouts)
  // maxW="container.lg" = 1200px
  "container.xs": { value: `${breakpoints.xs}px` }, // 576px
  "container.sm": { value: `${breakpoints.sm}px` }, // 768px
  "container.md": { value: `${breakpoints.md}px` }, // 1024px
  "container.lg": { value: `${breakpoints.lg}px` }, // 1200px
  "container.xl": { value: `${breakpoints.xl}px` }, // 1440px
  "container.content": { value: "1520px" }, // Standard content width
  "container.2xl": { value: `${breakpoints["2xl"]}px` }, // 1600px
  "container.3xl": { value: `${breakpoints["3xl"]}px` }, // 1720px
  "container.4xl": { value: `${breakpoints["4xl"]}px` }, // 1880px

  // Element sizes (for icons, buttons, avatars, small fixed elements)
  // boxSize="element.md" = 16px
  "element.2xs": { value: "4px" },
  "element.xs": { value: "8px" },
  "element.sm": { value: "12px" },
  "element.md": { value: "16px" },
  "element.lg": { value: "20px" },
  "element.xl": { value: "24px" },
  "element.2xl": { value: "32px" },
  "element.3xl": { value: "40px" },
  "element.4xl": { value: "48px" },
  "element.5xl": { value: "64px" },
  // TODO: Check why these are not defined in the design tokens in figma (80, 100, 128, 160)
  // Added these to remove the warnings for now until we have more time to diagnose the issue.
  // There are also other sizes that are not defined in the design tokens in figma (50, 100, 140, 128, 160)
  "element.6xl": { value: "80px" },
  "element.7xl": { value: "100px" },
  "element.8xl": { value: "128px" },
  "element.8.5xl": { value: "150px" },
  "element.9xl": { value: "160px" },
  "element.10xl": { value: "180px" },
  "element.11xl": { value: "200px" },
  "element.12xl": { value: "220px" },
  "element.13xl": { value: "240px" },
  "element.14xl": { value: "260px" },
  "element.15xl": { value: "280px" },
  "element.16xl": { value: "300px" },

  // Layout sizes (fixed dashboard dimensions)
  "layout.headerHeight": { value: HEADER_HEIGHT },
  "layout.sidebarWidth": { value: SIDEBAR_WIDTH },
  "layout.drawerWidth": { value: SUB_MENU_WIDTH },
  "layout.navbarDesktopHeight": { value: NAVBAR_DESKTOP_HEIGHT },
  "layout.navbarMobileHeight": { value: NAVBAR_MOBILE_HEIGHT },

  // Special values
  none: { value: "none" },
  0: { value: "0px" },
  full: { value: "100%" },
  min: { value: "min-content" },
  max: { value: "max-content" },
  fit: { value: "fit-content" },
};
const tableSlotRecipe = defineSlotRecipe({
  className: "table",
  slots: ["root", "header", "body", "row", "columnHeader", "cell", "footer"],
  base: {
    root: {
      fontVariantNumeric: "lining-nums tabular-nums",
      borderCollapse: "collapse",
      width: "full",
      textAlign: "start",
      verticalAlign: "top",
    },
    row: {
      _selected: {
        bg: "colorPalette.subtle",
      },
    },
    columnHeader: {
      fontWeight: "medium",
      textAlign: "start",
      color: "fg",
    },
    cell: {
      textAlign: "start",
      verticalAlign: "middle",
    },
  },
  variants: {
    variant: {
      // Custom "simple" variant: gray header, row borders, no outer border
      simple: {
        columnHeader: {
          bg: "bg.subtle",
          borderBottom: "1px solid",
          borderColor: "border",
        },
        cell: {
          borderBottom: "1px solid",
          borderColor: "border",
          bg: "bg",
          color: "fg",
        },
      },
      unstyled: {
        root: {
          // 1. Reset standard table properties
          border: "none",
          borderCollapse: "collapse",

          // 2. Force child elements to have zero borders using nested selectors
          // This overrides any specific 'cell' or 'row' base styles
          "& tr, & th, & td": {
            border: "none !important",
            borderBottom: "none !important",
            borderBottomWidth: "0px !important",
            boxShadow: "none !important",
            background: "transparent",
          },
        },
        // 3. Explicitly nullify the slots just in case
        header: { bg: "transparent", border: "none" },
        body: { bg: "transparent", border: "none" },
        row: { bg: "transparent", border: "none" },
        columnHeader: { bg: "transparent", border: "none" },
        cell: { bg: "transparent", border: "none" },
      },
    },
  },
});

const defaultShadows = {
  base: { value: "0px 4px 10px rgba(0, 0, 0, 0.2)" },
  strong: { value: "0px 4px 4px rgba(0, 0, 0, 0.2)" },
};

// Shared text selection styles used by both default and custom themes
// Uses *::selection to match Chakra v3's selector specificity (which sets colorPalette.emphasized)
// Only sets backgroundColor to Highlight system color - omitting color lets browser keep original text color
const selectionStyles = {
  "*::selection": {
    backgroundColor: "Highlight",
  },
  "input::selection, textarea::selection": {
    backgroundColor: "Highlight",
  },
};

// Extend the built-in Dialog slot recipe with comprehensive size variants.
// Chakra v3's default dialog recipe only defines xs-xl, cover, full.
//
// We add:
//  - 2xl-6xl sizes (maxW + maxH) for large purchase modals
//  - maxH for all sizes so the Dialog component can rely on the theme
//    instead of applying CSS !important overrides at runtime
//
// Both the legacy Modal component and the new Dialog component share this recipe.

const defaultDialogBorderRadius = "md";

const mobileFullScreenStyles = {
  maxW: "100vw",
  maxH: "100dvh",
  w: "100vw",
  h: "100dvh",
  borderRadius: "0",
  m: "0",
} as const;

const dialogMaxH = "calc(100dvh - 4rem)";

const dialogSize = (maxW: string) => ({
  content: {
    maxW,
    maxH: dialogMaxH,
    borderRadius: defaultDialogBorderRadius,
    smDown: mobileFullScreenStyles,
  },
});

const dialogSlotRecipe = defineSlotRecipe({
  className: "chakra-dialog",
  slots: [
    "backdrop",
    "positioner",
    "content",
    "header",
    "body",
    "footer",
    "title",
    "description",
    "closeTrigger",
  ],
  variants: {
    size: {
      // xs-compact does not go full screen on mobile - should be used for modals that are
      // on pages that are embeded into angular - like the email customizeation - send email preview modal.
      "xs-compact": {
        content: {
          maxW: "sm",
          maxH: dialogMaxH,
          borderRadius: defaultDialogBorderRadius,
          smDown: {
            maxW: "calc(100vw - 2rem)",
            mx: "md",
          },
        },
      },
      // Height is fit-content by default. maxH prevents viewport overflow.
      // Consumers needing a fixed height pass modalContentProps={{ h: "..." }}.
      xs: dialogSize("sm"),
      sm: dialogSize("md"),
      md: dialogSize("lg"),
      lg: dialogSize("2xl"),
      xl: dialogSize("4xl"),
      "2xl": dialogSize("2xl"),
      "3xl": dialogSize("3xl"),
      "4xl": dialogSize("4xl"),
      "5xl": dialogSize("5xl"),
      "6xl": dialogSize("6xl"),
      cover: {
        content: {
          maxW: "100vw",
          maxH: "100dvh",
          w: "100vw",
          h: "100dvh",
          borderRadius: "0",
          m: "0",
        },
      },
      full: {
        content: {
          maxW: "100vw",
          maxH: "100dvh",
          w: "100vw",
          h: "100dvh",
          borderRadius: "0",
          m: "0",
        },
      },
    },
  },
});

const baseConfig = defineConfig({
  theme: {
    keyframes: {
      shimmer: {
        "0%": { transform: "translateX(-100%)" },
        "100%": { transform: "translateX(100%)" },
      },
      calendarAnimation: {
        "0%, 100%": { opacity: 1 },
        "50%": { opacity: 0.5 },
      },
      slideDown: {
        "0%": { transform: "translateY(-50%)", opacity: 0 },
        "100%": { transform: "translateY(0)", opacity: 1 },
      },
      qrScanMove: {
        "0%, 5%, 100%": { top: "calc(var(--qr-spacing) * -1)" },
        "50%": { top: "calc(var(--qr-size) + var(--qr-spacing))" },
      },
      qrScanGradient: {
        "0%": { background: "var(--qr-color)" },
        "20%": {
          background: "linear-gradient(to top, var(--qr-color), transparent)",
        },
        "50%": { background: "var(--qr-color)" },
        "65%, 70%": {
          background:
            "linear-gradient(to bottom, var(--qr-color), transparent)",
        },
        "100%": { background: "var(--qr-color)" },
      },
      qrScanHeight: {
        "0%, 50%, 100%": { height: "2px" },
        "20%, 65%, 70%": { height: "20px" },
      },
      slideLeft: {
        "0%": { transform: "translateX(100%)", opacity: 0 },
        "100%": { transform: "translateX(0)", opacity: 1 },
      },
      slideRight: {
        "0%": { transform: "translateX(-100%)", opacity: 0 },
        "100%": { transform: "translateX(0)", opacity: 1 },
      },
    },
    tokens: {
      // Only truly static colors (white, black, third-party brands)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      colors: staticColors as any,
      fonts: {
        heading: { value: DEFAULT_FONT },
        body: { value: DEFAULT_FONT },
      },
      fontSizes: fontSizeTokens,
      spacing: spacingTokens,
      sizes: sizeTokens, // Uses breakpoint values: sm=768px, md=1024px, lg=1200px
      radii: radiiTokens,
      zIndex: zIndexTokens,
      shadows: defaultShadows,
      animations: { textShimmer: { value: "shimmer 2s linear infinite" } },
    },
    // ALL colors come from semantic tokens - they ALL adapt to dark mode
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    semanticTokens: semanticTokens as any,
    // Text styles for consistent typography (use textStyle="body.md" etc.)
    textStyles,
    breakpoints: breakpointsTheme,
    recipes: {
      link: linkRecipe,
      button: buildButtonRecipe(),
    },
    slotRecipes: {
      accordion: accordionSlotRecipe,
      dialog: dialogSlotRecipe,
      table: tableSlotRecipe,
      checkbox: checkboxSlotRecipe,
      tag: tagSlotRecipe,
    },
  },
  globalCss: {
    ":root": { "--100dvh": "90vh" },
    "@supports (height: 100dvh)": {
      ":root": { "--100dvh": "100dvh" },
    },
    html: {
      bg: "bg",
      height: "100%",
      scrollBehavior: "smooth",
    },
    body: {
      bg: "bg",
      color: "fg",
      fontSize: "md", // 16px - default for all text
    },
    ...selectionStyles,
  },
} as Parameters<typeof defineConfig>[0]);

// =============================================================================
// EXPORTS
// =============================================================================

// DEFAULT SYSTEM - Created once, same object for SSR and client
export const defaultSystem = createSystem(defaultConfig, baseConfig);

// For dynamic theming (custom colors/fonts from branding config or URL params)
// Creates semantic tokens from custom color objects with light/dark variants
/**
 * Generate a custom theme with overridden primary/secondary colors and/or fonts.
 * Uses the same generateFullPalette() function as the default theme
 * to ensure consistent token structure.
 *
 * Fonts are baked into the system tokens so Chakra generates the correct
 * CSS variables directly - no inline <style> override needed.
 */
export default function generateTheme(opts: {
  secondaryColor?: ColorPalette;
  primaryColor?: ColorPalette;
  /** Custom heading font family (e.g. "Poppins", "Montserrat") */
  headingFont?: string;
  /** Custom body font family (e.g. "Open Sans", "Roboto") */
  bodyFont?: string;
  /** Layer 1 token-scale overrides */
  tokens?: ThemeTokenOverrides;
  /** Layer 2 component default-token overrides */
  components?: ThemeComponentOverrides;
}) {
  if (
    !opts.primaryColor &&
    !opts.secondaryColor &&
    !opts.headingFont &&
    !opts.bodyFont &&
    !opts.tokens &&
    !opts.components
  ) {
    return defaultSystem;
  }

  // Build custom semantic tokens using the SAME generators as default theme
  const customSemanticTokens = {
    colors: {
      ...semanticTokens.colors,
      // Override primary palette with ALL tokens (numbered + semantic)
      ...(opts.primaryColor && {
        primary: generateFullPalette(opts.primaryColor),
        ...generateBgSubtleToken(opts.primaryColor, "primary"),
      }),
      // Override secondary palette with ALL tokens (numbered + semantic)
      ...(opts.secondaryColor && {
        secondary: generateFullPalette(opts.secondaryColor),
        ...generateBgSubtleToken(opts.secondaryColor, "secondary"),
      }),
      ...buildSemanticColorOverrides(opts.tokens?.semanticColors),
    },
  };

  const mergedFontSizeTokens = mergeTokenObjects(
    fontSizeTokens,
    opts.tokens?.fontSizes
  );
  const mergedLineHeightTokens = mergeTokenObjects(
    lineHeightTokens,
    opts.tokens?.lineHeights
  );
  const mergedFontWeightTokens = mergeTokenObjects(
    fontWeightTokens,
    opts.tokens?.fontWeights
  );
  const mergedSpacingTokens = mergeTokenObjects(
    spacingTokens,
    opts.tokens?.spacing
  );
  const mergedRadiiTokens = mergeRadiiTokenObjects(
    radiiTokens,
    opts.tokens?.radii
  );
  const mergedShadows = mergeTokenObjects(defaultShadows, opts.tokens?.shadows);
  // Always install the Showpass button recipe, even when no button overrides
  // are provided. Otherwise Chakra falls back to its built-in button recipe,
  // whose size scale is shifted one step smaller than ours (e.g. size="md"
  // renders at `textStyle: "sm"` = 14px instead of our `fontSize: "md"` = 16px),
  // which made buttons appear smaller on branded themes.
  const customRecipes = {
    link: linkRecipe,
    button: buildButtonRecipe(opts.components?.button),
  };

  const customConfig = defineConfig({
    theme: {
      tokens: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        colors: staticColors as any,
        fonts: {
          heading: { value: buildFontStack(opts.headingFont) },
          body: { value: buildFontStack(opts.bodyFont) },
        },
        fontSizes: mergedFontSizeTokens,
        lineHeights: mergedLineHeightTokens,
        fontWeights: mergedFontWeightTokens,
        spacing: mergedSpacingTokens,
        sizes: sizeTokens,
        radii: mergedRadiiTokens,
        zIndex: zIndexTokens,
        shadows: mergedShadows,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      semanticTokens: customSemanticTokens as any,
      textStyles,
      breakpoints: breakpointsTheme,
      recipes: customRecipes,
      slotRecipes: {
        accordion: accordionSlotRecipe,
        table: tableSlotRecipe,
        checkbox: checkboxSlotRecipe,
        tag: tagSlotRecipe,
        dialog: dialogSlotRecipe,
      },
    },
    globalCss: {
      ":root": { "--100dvh": "90vh" },
      "@supports (height: 100dvh)": {
        ":root": { "--100dvh": "100dvh" },
      },
      html: {
        bg: "bg",
        height: "100%",
        scrollBehavior: "smooth",
      },
      body: {
        bg: "bg",
        color: "fg",
        fontSize: "md", // 16px - default for all text
      },
      ...selectionStyles,
    },
  } as Parameters<typeof defineConfig>[0]);

  return createSystem(defaultConfig, customConfig);
}
